import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Pencil, X, Plus, Download } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAuthContext } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { formatCents, formatDate } from '@/lib/utils';
import AttachmentsPanel from '@/components/AttachmentsPanel';
import { linkClass } from '@/components/ExportButtons';
import { SortableTh } from '@/components/SortableTh';
import { useClientSort, type SortValue } from '@/hooks/useClientSort';
import { ESTIMATE_STATUS_BADGE, COUNTERTOP_JOB_STATUS_BADGE } from '@/lib/badges';
import type { CountertopJobStatusUI } from '@/lib/api-types';
import type { Customer, EstimateStatus } from '@billflow/shared';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface CustomerResponse {
  data: { customer: Customer };
}

interface StoreCreditEntry {
  id: number;
  amount: number;
  source_type: string;
  source_id: number | null;
  notes: string | null;
  created_at: string;
}

interface LedgerEstimate {
  id: number;
  number: string;
  date: string;
  status: EstimateStatus;
  total: number;
  amount_paid: number;
  balance: number;
}

interface LedgerPayment {
  id: number;
  date: string;
  amount: number;
  store_credit_applied: number;
  estimate_id: number | null;
  estimate_number: string | null;
}

interface LedgerCountertopJob {
  id: number;
  number: string;
  status: string;
  measurement_date: string | null;
  install_date: string | null;
  estimate_id: number | null;
  estimate_number: string | null;
  po_id: number | null;
  approx_quote: number | null;
  created_at: string;
}

interface LedgerResponse {
  data: {
    store_credit_balance: number;
    outstanding_balance: number;
    estimates: LedgerEstimate[];
    payments: LedgerPayment[];
    store_credit_entries: StoreCreditEntry[];
    countertop_jobs: LedgerCountertopJob[];
  };
}

// ─── Edit Modal ────────────────────────────────────────────────────────────────

interface EditModalProps {
  customer: Customer;
  onClose: () => void;
  onSaved: () => void;
}

function EditModal({ customer, onClose, onSaved }: EditModalProps) {
  const [code, setCode] = useState(customer.code ?? '');
  const [name, setName] = useState(customer.name);
  const [contactPerson, setContactPerson] = useState(customer.contact_person ?? '');
  const [phone, setPhone] = useState(customer.phone ?? '');
  const [email, setEmail] = useState(customer.email ?? '');
  const [address, setAddress] = useState(customer.address ?? '');
  const [creditLimitDollars, setCreditLimitDollars] = useState(String(customer.credit_limit / 100));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const creditCents = creditLimitDollars ? Math.round(parseFloat(creditLimitDollars) * 100) : 0;
      await apiFetch(`/api/customers/${customer.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          code: code.trim() || undefined,
          name: name.trim(),
          // Send '' (not undefined) so clearing an optional field persists — JSON.stringify
          // drops undefined keys, which on PATCH means "leave unchanged" (can't remove a value).
          contact_person: contactPerson.trim(),
          phone: phone.trim(),
          email: email.trim(),
          address: address.trim(),
          credit_limit: creditCents,
        }),
      });
      toast.success('Customer updated');
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Edit Customer</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="ec-code">Code</Label>
              <Input
                id="ec-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Auto-generated if blank"
                disabled={loading}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ec-name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ec-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={loading}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ec-contact">Contact Person</Label>
            <Input
              id="ec-contact"
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="ec-phone">Phone</Label>
              <Input
                id="ec-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ec-email">Email</Label>
              <Input
                id="ec-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ec-address">Address</Label>
            <textarea
              id="ec-address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              disabled={loading}
              rows={2}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ec-credit">Credit Limit ($)</Label>
            <Input
              id="ec-credit"
              type="number"
              min="0"
              step="0.01"
              value={creditLimitDollars}
              onChange={(e) => setCreditLimitDollars(e.target.value)}
              placeholder="0.00"
              disabled={loading}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Adjust Store Credit Modal ────────────────────────────────────────────────

interface AdjustStoreCreditModalProps {
  customerId: string;
  currentBalance: number;
  onClose: () => void;
  onSaved: () => void;
}

function AdjustStoreCreditModal({
  customerId,
  currentBalance,
  onClose,
  onSaved,
}: AdjustStoreCreditModalProps) {
  const [direction, setDirection] = useState<'add' | 'deduct'>('add');
  const [amountStr, setAmountStr] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const amount = Math.round((parseFloat(amountStr) || 0) * 100);
    if (amount <= 0) {
      setError('Amount must be greater than zero.');
      return;
    }
    if (!reason.trim()) {
      setError('Reason is required.');
      return;
    }
    setLoading(true);
    try {
      await apiFetch(`/api/customers/${customerId}/store-credit`, {
        method: 'POST',
        body: JSON.stringify({ direction, amount, notes: reason.trim() }),
      });
      toast.success(direction === 'add' ? 'Store credit added' : 'Store credit deducted');
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Adjust Store Credit</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-xs text-muted-foreground">
            Current balance:{' '}
            <span className="font-medium tabular-nums text-foreground">
              {formatCents(currentBalance)}
            </span>
          </p>
          <div className="space-y-1.5">
            <Label>Direction</Label>
            <div className="flex rounded-md border overflow-hidden">
              {(['add', 'deduct'] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDirection(d)}
                  disabled={loading}
                  className={`flex-1 py-2 text-sm font-medium transition-colors ${
                    direction === d
                      ? d === 'add'
                        ? 'bg-green-600 text-white'
                        : 'bg-destructive text-destructive-foreground'
                      : 'bg-background text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {d === 'add' ? 'Add' : 'Deduct'}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="adj-amount">
              Amount ($) <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                $
              </span>
              <Input
                id="adj-amount"
                type="number"
                min="0.01"
                step="0.01"
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                className="pl-7 tabular-nums"
                required
                disabled={loading}
                placeholder="0.00"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="adj-reason">
              Reason <span className="text-destructive">*</span>
            </Label>
            <textarea
              id="adj-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              disabled={loading}
              rows={3}
              placeholder="e.g. Goodwill adjustment for damaged goods"
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving…' : 'Apply Adjustment'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Source type label helper ─────────────────────────────────────────────────

function formatSourceType(source_type: string): string {
  switch (source_type) {
    case 'sales_return':
      return 'Sales Return';
    case 'payment_applied':
      return 'Payment Applied';
    case 'adjustment':
      return 'Adjustment';
    case 'purchase_return':
      return 'Purchase Return';
    default:
      return source_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

// ─── Info field helper ─────────────────────────────────────────────────────────

function InfoField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</dt>
      <dd className="text-sm text-foreground">
        {value ?? <span className="text-muted-foreground">—</span>}
      </dd>
    </div>
  );
}

// ─── Tab types ─────────────────────────────────────────────────────────────────

type Tab = 'estimates' | 'payments' | 'store-credit' | 'countertop';

const tabs: { label: string; value: Tab }[] = [
  { label: 'Estimates', value: 'estimates' },
  { label: 'Payments', value: 'payments' },
  { label: 'Store Credit', value: 'store-credit' },
  { label: 'Countertop', value: 'countertop' },
];

// ─── Sort accessors (stable module constants — one per tab table) ───────────────

const ESTIMATE_ACCESSORS: Record<string, (row: LedgerEstimate) => SortValue> = {
  number: (r) => r.number,
  date: (r) => r.date,
  status: (r) => r.status,
  total: (r) => r.total,
  amount_paid: (r) => r.amount_paid,
  balance: (r) => r.total - r.amount_paid,
};

const PAYMENT_ACCESSORS: Record<string, (row: LedgerPayment) => SortValue> = {
  date: (r) => r.date,
  amount: (r) => r.amount,
  store_credit_applied: (r) => r.store_credit_applied,
  estimate_number: (r) => r.estimate_number,
};

const STORE_CREDIT_ACCESSORS: Record<string, (row: StoreCreditEntry) => SortValue> = {
  created_at: (r) => r.created_at,
  source_type: (r) => formatSourceType(r.source_type),
  amount: (r) => r.amount,
};

const COUNTERTOP_ACCESSORS: Record<string, (row: LedgerCountertopJob) => SortValue> = {
  number: (r) => r.number,
  status: (r) => r.status,
  measurement_date: (r) => r.measurement_date,
  install_date: (r) => r.install_date,
  created_at: (r) => r.created_at,
};

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('estimates');
  const [adjustCreditOpen, setAdjustCreditOpen] = useState(false);

  const {
    data: customerData,
    isLoading: customerLoading,
    error: customerError,
  } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => apiFetch<CustomerResponse>(`/api/customers/${id}`),
    enabled: !!id,
  });

  const { data: ledgerData, isLoading: ledgerLoading } = useQuery({
    queryKey: ['customer-ledger', id],
    queryFn: () => apiFetch<LedgerResponse>(`/api/customers/${id}/ledger`),
    enabled: !!id,
  });

  // Client-side sorting for each tab table. Hooks stay top-level (never inside a
  // tab-conditional); defaults preserve the server's returned order.
  const ledgerRows = ledgerData?.data;
  const estimatesSort = useClientSort(ledgerRows?.estimates ?? [], {
    defaultKey: 'date',
    defaultDir: 'desc',
    accessors: ESTIMATE_ACCESSORS,
  });
  const paymentsSort = useClientSort(ledgerRows?.payments ?? [], {
    defaultKey: 'date',
    defaultDir: 'desc',
    accessors: PAYMENT_ACCESSORS,
  });
  const storeCreditSort = useClientSort(ledgerRows?.store_credit_entries ?? [], {
    defaultKey: 'created_at',
    defaultDir: 'desc',
    accessors: STORE_CREDIT_ACCESSORS,
  });
  const countertopSort = useClientSort(ledgerRows?.countertop_jobs ?? [], {
    defaultKey: 'created_at',
    defaultDir: 'desc',
    accessors: COUNTERTOP_ACCESSORS,
  });

  function refetch() {
    queryClient.invalidateQueries({ queryKey: ['customer', id] });
    queryClient.invalidateQueries({ queryKey: ['customer-ledger', id] });
    queryClient.invalidateQueries({ queryKey: ['customers'] });
  }

  if (customerLoading) {
    return (
      <div className="p-6">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (customerError || !customerData?.data.customer) {
    return (
      <div className="p-6">
        <Link
          to="/customers"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Customers
        </Link>
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {customerError instanceof Error ? customerError.message : 'Customer not found.'}
        </div>
      </div>
    );
  }

  const customer = customerData.data.customer;
  const ledger = ledgerData?.data;

  return (
    <div className="p-6 max-w-6xl">
      {/* Back nav */}
      <Link
        to="/customers"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-5"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Customers
      </Link>

      {/* Top row: customer info (left) + account summary (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Info Card */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3 flex-wrap">
                <CardTitle className="text-xl">{customer.name}</CardTitle>
                {customer.status === 'active' ? (
                  <Badge variant="green">Active</Badge>
                ) : (
                  <Badge variant="red">Inactive</Badge>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={`/api/customers/${id}/statement/pdf?download=1`}
                  download
                  className={linkClass}
                >
                  <Download className="h-3 w-3" />
                  Download Statement
                </a>
                {isAdmin && (
                  <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                    <Pencil className="h-3.5 w-3.5 mr-1.5" />
                    Edit
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-4">
              <InfoField label="Code" value={customer.code} />
              <InfoField label="Contact Person" value={customer.contact_person} />
              <InfoField label="Phone" value={customer.phone} />
              <InfoField label="Email" value={customer.email} />
              {customer.address && (
                <div className="col-span-2 sm:col-span-3 space-y-0.5">
                  <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Address
                  </dt>
                  <dd className="text-sm text-foreground whitespace-pre-line">
                    {customer.address}
                  </dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>

        {/* Account summary */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Account summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
                Outstanding Balance
              </div>
              <div className="text-3xl font-semibold tabular-nums">
                {ledgerLoading ? '…' : formatCents(ledger?.outstanding_balance ?? 0)}
              </div>
            </div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
              <InfoField
                label="Store Credit"
                value={formatCents(ledger?.store_credit_balance ?? 0)}
              />
              <InfoField label="Credit Limit" value={formatCents(customer.credit_limit)} />
            </dl>
          </CardContent>
        </Card>
      </div>

      {/* Ledger Tabs */}
      <div>
        {/* Tab bar */}
        <div className="flex border-b mb-4">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setActiveTab(tab.value)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.value
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'estimates' && (
          <div className="rounded-md border overflow-hidden">
            {ledgerLoading ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : !ledger?.estimates?.length ? (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                No estimates yet.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <SortableTh
                      label="Number"
                      sortKey="number"
                      sort={estimatesSort.sort}
                      dir={estimatesSort.dir}
                      onToggle={estimatesSort.toggleSort}
                    />
                    <SortableTh
                      label="Date"
                      sortKey="date"
                      sort={estimatesSort.sort}
                      dir={estimatesSort.dir}
                      onToggle={estimatesSort.toggleSort}
                    />
                    <SortableTh
                      label="Status"
                      sortKey="status"
                      sort={estimatesSort.sort}
                      dir={estimatesSort.dir}
                      onToggle={estimatesSort.toggleSort}
                    />
                    <SortableTh
                      label="Total"
                      sortKey="total"
                      align="right"
                      sort={estimatesSort.sort}
                      dir={estimatesSort.dir}
                      onToggle={estimatesSort.toggleSort}
                    />
                    <SortableTh
                      label="Paid"
                      sortKey="amount_paid"
                      align="right"
                      sort={estimatesSort.sort}
                      dir={estimatesSort.dir}
                      onToggle={estimatesSort.toggleSort}
                    />
                    <SortableTh
                      label="Balance"
                      sortKey="balance"
                      align="right"
                      sort={estimatesSort.sort}
                      dir={estimatesSort.dir}
                      onToggle={estimatesSort.toggleSort}
                    />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {estimatesSort.sorted.map((est) => {
                    const badge = ESTIMATE_STATUS_BADGE[est.status];
                    return (
                      <tr key={est.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          <Link
                            to={`/estimates/${est.id}`}
                            className="font-mono text-primary hover:underline"
                          >
                            {est.number}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{formatDate(est.date)}</td>
                        <td className="px-4 py-3">
                          <Badge variant={badge.variant}>{badge.label}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {formatCents(est.total)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {formatCents(est.amount_paid)}
                        </td>
                        <td
                          className={`px-4 py-3 text-right tabular-nums font-medium ${est.balance > 0 ? 'text-destructive' : ''}`}
                        >
                          {formatCents(est.balance)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="rounded-md border overflow-hidden">
            {ledgerLoading ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : !ledger?.payments?.length ? (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                No payments recorded yet.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <SortableTh
                      label="Date"
                      sortKey="date"
                      sort={paymentsSort.sort}
                      dir={paymentsSort.dir}
                      onToggle={paymentsSort.toggleSort}
                    />
                    <SortableTh
                      label="Amount"
                      sortKey="amount"
                      align="right"
                      sort={paymentsSort.sort}
                      dir={paymentsSort.dir}
                      onToggle={paymentsSort.toggleSort}
                    />
                    {ledger.payments.some((p) => p.store_credit_applied > 0) && (
                      <SortableTh
                        label="Store Credit Applied"
                        sortKey="store_credit_applied"
                        align="right"
                        sort={paymentsSort.sort}
                        dir={paymentsSort.dir}
                        onToggle={paymentsSort.toggleSort}
                      />
                    )}
                    <SortableTh
                      label="Estimate"
                      sortKey="estimate_number"
                      sort={paymentsSort.sort}
                      dir={paymentsSort.dir}
                      onToggle={paymentsSort.toggleSort}
                    />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {paymentsSort.sorted.map((pmt) => (
                    <tr key={pmt.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 text-muted-foreground">{formatDate(pmt.date)}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium">
                        {formatCents(pmt.amount)}
                      </td>
                      {ledger.payments.some((p) => p.store_credit_applied > 0) && (
                        <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                          {pmt.store_credit_applied > 0
                            ? formatCents(pmt.store_credit_applied)
                            : '—'}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        {pmt.estimate_id && pmt.estimate_number ? (
                          <Link
                            to={`/estimates/${pmt.estimate_id}`}
                            className="font-mono text-primary hover:underline"
                          >
                            {pmt.estimate_number}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'store-credit' && (
          <div className="rounded-md border">
            {ledgerLoading ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : (
              <>
                {/* Balance header row */}
                <div className="flex items-center justify-between px-6 py-4 border-b bg-muted/10">
                  <div>
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                      Current Balance
                    </div>
                    <div className="text-2xl font-semibold tabular-nums">
                      {formatCents(ledger?.store_credit_balance ?? 0)}
                    </div>
                  </div>
                  {isAdmin && (
                    <Button variant="outline" size="sm" onClick={() => setAdjustCreditOpen(true)}>
                      <Plus className="h-3.5 w-3.5 mr-1.5" />
                      Adjust Store Credit
                    </Button>
                  )}
                </div>

                {/* Ledger entries table */}
                {!ledger?.store_credit_entries?.length ? (
                  <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                    No store credit activity yet.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/20">
                          <SortableTh
                            label="Date"
                            sortKey="created_at"
                            className="px-3 py-2.5 text-xs"
                            sort={storeCreditSort.sort}
                            dir={storeCreditSort.dir}
                            onToggle={storeCreditSort.toggleSort}
                          />
                          <SortableTh
                            label="Type"
                            sortKey="source_type"
                            className="px-3 py-2.5 text-xs"
                            sort={storeCreditSort.sort}
                            dir={storeCreditSort.dir}
                            onToggle={storeCreditSort.toggleSort}
                          />
                          <SortableTh
                            label="Amount"
                            sortKey="amount"
                            align="right"
                            className="px-3 py-2.5 text-xs"
                            sort={storeCreditSort.sort}
                            dir={storeCreditSort.dir}
                            onToggle={storeCreditSort.toggleSort}
                          />
                          <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                            Notes
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {storeCreditSort.sorted.map((entry) => (
                          <tr
                            key={entry.id}
                            className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                          >
                            <td className="px-3 py-2.5 tabular-nums text-sm">
                              {formatDate(entry.created_at)}
                            </td>
                            <td className="px-3 py-2.5 text-sm">
                              {formatSourceType(entry.source_type)}
                            </td>
                            <td
                              className={`px-3 py-2.5 text-right tabular-nums text-sm font-medium ${
                                entry.amount >= 0
                                  ? 'text-green-700 dark:text-green-400'
                                  : 'text-destructive'
                              }`}
                            >
                              {entry.amount >= 0 ? '+' : ''}
                              {formatCents(entry.amount)}
                            </td>
                            <td className="px-3 py-2.5 text-sm text-muted-foreground">
                              {entry.notes ?? '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'countertop' && (
          <div className="rounded-md border overflow-hidden">
            {ledgerLoading ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : !ledger?.countertop_jobs?.length ? (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                No countertop jobs.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <SortableTh
                      label="Number"
                      sortKey="number"
                      sort={countertopSort.sort}
                      dir={countertopSort.dir}
                      onToggle={countertopSort.toggleSort}
                    />
                    <SortableTh
                      label="Status"
                      sortKey="status"
                      sort={countertopSort.sort}
                      dir={countertopSort.dir}
                      onToggle={countertopSort.toggleSort}
                    />
                    <SortableTh
                      label="Measurement"
                      sortKey="measurement_date"
                      sort={countertopSort.sort}
                      dir={countertopSort.dir}
                      onToggle={countertopSort.toggleSort}
                    />
                    <SortableTh
                      label="Install"
                      sortKey="install_date"
                      sort={countertopSort.sort}
                      dir={countertopSort.dir}
                      onToggle={countertopSort.toggleSort}
                    />
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                      Estimate
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {countertopSort.sorted.map((job) => {
                    const badge = COUNTERTOP_JOB_STATUS_BADGE[job.status as CountertopJobStatusUI];
                    return (
                      <tr key={job.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          <Link
                            to={`/countertop-jobs/${job.id}`}
                            className="font-mono text-primary hover:underline"
                          >
                            {job.number}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          {badge ? (
                            <Badge variant={badge.variant}>{badge.label}</Badge>
                          ) : (
                            <span className="text-muted-foreground">{job.status}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {job.measurement_date ? formatDate(job.measurement_date) : '—'}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {job.install_date ? formatDate(job.install_date) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          {job.estimate_id ? (
                            <Link
                              to={`/estimates/${job.estimate_id}`}
                              className="font-mono text-primary hover:underline"
                            >
                              {job.estimate_number ?? `#${job.estimate_id}`}
                            </Link>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Attachments & Notes */}
      <div className="mt-6">
        <AttachmentsPanel module="customer" recordId={Number(id)} />
      </div>

      {/* Edit modal */}
      {editOpen && (
        <EditModal customer={customer} onClose={() => setEditOpen(false)} onSaved={refetch} />
      )}

      {/* Adjust Store Credit modal (Admin only) */}
      {adjustCreditOpen && isAdmin && (
        <AdjustStoreCreditModal
          customerId={id!}
          currentBalance={ledger?.store_credit_balance ?? 0}
          onClose={() => setAdjustCreditOpen(false)}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ['customer-ledger', id] })}
        />
      )}
    </div>
  );
}
